import { AsyncThunkAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { useAppDispatch } from './hooks';

/**
 * Текст тоста: либо готовая строка, либо функция от результата (или ошибки).
 * Функция может вернуть `undefined` — тогда тост не показывается. Это нужно для
 * условных сообщений: например, показать ошибку только для одного конкретного случая.
 */
type ToastMessage<T> = string | ((value: T) => string | undefined);

export interface DispatchToastMessages<Returned> {
  success?: ToastMessage<Returned>;
  error?: ToastMessage<unknown>;
  successOptions?: ToastOptions;
  errorOptions?: ToastOptions;
}

function resolveMessage<T>(message: ToastMessage<T> | undefined, value: T): string | undefined {
  if (message === undefined) return undefined;
  return typeof message === 'function' ? message(value) : message;
}

/**
 * Диспатчит async thunk и показывает тост по его результату.
 *
 * Уведомления — часть UI, поэтому reducer'ы и thunk'и о них не знают: текст задаёт то место,
 * которое вызывает действие, и один и тот же thunk может сообщать о себе по-разному на разных
 * экранах.
 *
 * Ошибка **не пробрасывается** дальше: она уже показана пользователю. Вместо этого возвращается
 * `undefined` — большинство мест вызова живут в `useEffect` без `catch`, и проброс превратился бы
 * там в unhandled rejection. Если результат нужен, проверяйте его на `undefined`.
 *
 * @example
 * const dispatchWithToast = useDispatchWithToast();
 *
 * // загрузка: сообщаем только о провале
 * dispatchWithToast(fetchLocation({ id }), { error: 'Не удалось загрузить локацию' });
 *
 * // сохранение: нужен результат
 * const location = await dispatchWithToast(upsertLocation(data), {
 *   success: 'Локация успешно сохранена',
 *   error: 'Не удалось сохранить локацию',
 * });
 * if (!location) return;
 * navigate(Pages.LOCATIONS.builders.edit(location.id));
 */
export function useDispatchWithToast() {
  const dispatch = useAppDispatch();

  return useCallback(
    async <Returned>(
      action: AsyncThunkAction<Returned, any, any>,
      messages: DispatchToastMessages<Returned> = {},
    ): Promise<Returned | undefined> => {
      try {
        const result = await dispatch(action).unwrap();

        const successMessage = resolveMessage(messages.success, result);
        if (successMessage) {
          toast.success(successMessage, messages.successOptions);
        }

        return result;
      } catch (error) {
        const errorMessage = resolveMessage(messages.error, error);
        if (errorMessage) {
          toast.error(errorMessage, messages.errorOptions);
        }

        return undefined;
      }
    },
    [dispatch],
  );
}
